'use es6';
/**
 * TODO:
 * When changing this component's propTypes,
 * update its corresponding .d.ts file. This
 * file is not autogenerated; it is manually
 * maintained.
 *
 * Once this component has been converted to
 * TypeScript, remove this comment.
 * https://hubspot.slack.com/archives/G1WJWMNMQ/p1665775630614649
 */

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsx as _jsx } from "react/jsx-runtime";
import I18n from 'I18n';
import PropTypes from 'prop-types';
import { useContext, memo } from 'react';
import useUniqueId from 'react-utils/hooks/useUniqueId';
import { FieldsetContext } from '../context/FieldsetContext';
import { isDateInRange, isValid } from '../core/SimpleDate';
import SyntheticEvent from '../core/SyntheticEvent';
import Controllable from '../decorators/Controllable';
import ShareInput from '../decorators/ShareInput';
import UIInputIconWrapper from '../input/internal/UIInputIconWrapper';
import UITextInput from '../input/UITextInput';
import { SimpleDateType } from '../types/SimpleDateTypes';
import { listAttrIncludes } from '../utils/Dom';
import { preventDefaultHandler } from '../utils/DomEvents';
import { getDatePlaceholder, getLocalizedDateFormat } from '../utils/Locale';
import createLazyPropType from '../utils/propTypes/createLazyPropType';
import { hidden } from '../utils/propTypes/decorators';
import deprecated from '../utils/propTypes/deprecated';
import refObject from '../utils/propTypes/refObject';
import AbstractCalendar from './internal/AbstractCalendar';
import CalendarPagePopover from './internal/CalendarPagePopover';
import UsesToday from './internal/UsesToday';

const isValidValue = (value, minValue, maxValue) => {
  if (value == null) return true;
  if (!isValid(value)) return false;
  return isDateInRange(value, minValue, maxValue);
};

const UIDateInput = /*#__PURE__*/memo(props => {
  const {
    'aria-invalid': ariaInvalid,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-required': ariaRequired,
    autoFocus,
    calendarPage,
    className,
    clearLabel,
    disabled,
    disabledValues,
    error,
    focusedValue,
    format,
    iconSize,
    id,
    Input,
    inputRef,
    inputWidth,
    min,
    minValue,
    max,
    maxValue,
    maxValidationMessage,
    maxValueValidationMessage,
    minValidationMessage,
    minValueValidationMessage,
    name,
    onCalendarPageChange,
    onChange,
    onOpenChange,
    open,
    readOnly,
    required,
    today,
    todayLabel,
    use,
    value
  } = props,
        rest = _objectWithoutPropertiesLoose(props, ["aria-invalid", "aria-label", "aria-labelledby", "aria-required", "autoFocus", "calendarPage", "className", "clearLabel", "disabled", "disabledValues", "error", "focusedValue", "format", "iconSize", "id", "Input", "inputRef", "inputWidth", "min", "minValue", "max", "maxValue", "maxValidationMessage", "maxValueValidationMessage", "minValidationMessage", "minValueValidationMessage", "name", "onCalendarPageChange", "onChange", "onFocusedValueChange", "onOpenChange", "open", "readOnly", "required", "today", "todayLabel", "use", "value"]);

  const calendarId = useUniqueId('dateinput-calendar-');
  const popoverId = useUniqueId('dateinput-popover-');
  const fieldsetContext = useContext(FieldsetContext);
  const computedDisabled = disabled || fieldsetContext.disabled;

  const triggerOpen = () => {
    if (readOnly || computedDisabled) return;
    onOpenChange(SyntheticEvent(true));
  };

  return /*#__PURE__*/_jsx(AbstractCalendar, Object.assign({}, props, {
    children: ({
      onInputChange,
      onKeyDown,
      onKeyUp,
      onBlur,
      inputValue
    }) => /*#__PURE__*/_jsx(CalendarPagePopover, {
      calendarId: calendarId,
      calendarPage: calendarPage,
      clearLabel: clearLabel,
      disabledValues: disabledValues,
      focusedValue: focusedValue,
      id: popoverId,
      max: max || maxValue,
      min: min || minValue,
      maxValidationMessage: maxValidationMessage || maxValueValidationMessage,
      minValidationMessage: minValidationMessage || minValueValidationMessage,
      onCalendarPageChange: onCalendarPageChange,
      onChange: onChange,
      onFocusLeave: () => {
        onOpenChange(SyntheticEvent(false));
      },
      onMouseDown: preventDefaultHandler
      /* keep input focused */
      ,
      onOpenChange: onOpenChange,
      open: open && !computedDisabled && !readOnly,
      today: today,
      todayLabel: todayLabel,
      value: value,
      children: /*#__PURE__*/_jsx(UIInputIconWrapper, Object.assign({}, rest, {
        className: className,
        disabled: computedDisabled,
        iconName: "date",
        size: iconSize || fieldsetContext.size,
        children: /*#__PURE__*/_jsx(Input, {
          "aria-invalid": error ? 'true' : ariaInvalid,
          "aria-label": ariaLabel,
          "aria-labelledby": ariaLabelledBy,
          "aria-required": ariaRequired,
          autoComplete: "off",
          autoFocus: autoFocus,
          className: listAttrIncludes(className, 'private-form__control--inline') ? 'private-form__control--inline' : undefined,
          "data-format": getLocalizedDateFormat(format),
          disabled: computedDisabled,
          error: error || !isValidValue(value, minValue, maxValue),
          id: id,
          inputRef: inputRef,
          name: name,
          onBlur: onBlur,
          onChange: onInputChange,
          onClick: triggerOpen,
          onFocus: evt => {
            if (readOnly || computedDisabled) return;
            evt.target.select(); // inputRef is not yet available (see facebook/react#7769)

            triggerOpen();
          },
          onKeyDown: onKeyDown,
          onKeyUp: onKeyUp,
          placeholder: getDatePlaceholder(format),
          readOnly: readOnly,
          required: required,
          use: use,
          value: inputValue,
          width: inputWidth
        })
      }))
    })
  }));
});

const deprecatedMinMaxMessage = propName => {
  const newPropName = propName.replace('Value', '');
  return {
    message: `UIDateInput: The \`${propName}\` prop is deprecated. Use \`${newPropName}\` instead.`,
    key: 'UIDateInput-old-minmax'
  };
};

UIDateInput.propTypes = {
  'aria-invalid': PropTypes.string,
  'aria-label': PropTypes.string,
  'aria-labelledby': PropTypes.string,
  'aria-required': PropTypes.bool,
  autoFocus: PropTypes.bool,
  calendarPage: hidden(PropTypes.shape({
    year: PropTypes.number.isRequired,
    month: PropTypes.number.isRequired
  })),
  clearLabel: createLazyPropType(PropTypes.string),
  disabled: PropTypes.bool,
  disabledValues: PropTypes.arrayOf(SimpleDateType),
  error: PropTypes.bool,
  focusedValue: hidden(SimpleDateType),
  format: PropTypes.oneOf(['L', 'YYYY-MM-DD', 'LL', 'll']).isRequired,
  iconSize: hidden(PropTypes.number),
  id: PropTypes.string,
  Input: hidden(PropTypes.elementType.isRequired),
  inputRef: refObject.isRequired,
  inputWidth: hidden(PropTypes.number),
  max: SimpleDateType,
  maxValidationMessage: createLazyPropType(PropTypes.string),
  maxValue: deprecated(SimpleDateType, deprecatedMinMaxMessage),
  maxValueValidationMessage: deprecated(createLazyPropType(PropTypes.string), deprecatedMinMaxMessage),
  min: SimpleDateType,
  minValue: deprecated(SimpleDateType, deprecatedMinMaxMessage),
  minValidationMessage: createLazyPropType(PropTypes.string),
  minValueValidationMessage: deprecated(createLazyPropType(PropTypes.string), deprecatedMinMaxMessage),
  name: PropTypes.string,
  onCalendarPageChange: hidden(PropTypes.func.isRequired),
  onChange: PropTypes.func.isRequired,
  onFocusedValueChange: hidden(PropTypes.func.isRequired),
  onOpenChange: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  readOnly: PropTypes.bool,
  required: PropTypes.bool,
  today: SimpleDateType.isRequired,
  todayLabel: createLazyPropType(PropTypes.string),
  use: UITextInput.propTypes.use,
  value: SimpleDateType
};
UIDateInput.defaultProps = {
  clearLabel: () => I18n.text('ui.datePicker.clear'),
  format: 'L',
  Input: UITextInput,
  maxValidationMessage: () => I18n.text('ui.datePicker.dateAboveMax'),
  minValidationMessage: () => I18n.text('ui.datePicker.dateBelowMin'),
  open: false,
  todayLabel: () => I18n.text('ui.datePicker.today')
};
UIDateInput.displayName = 'UIDateInput';
export default ShareInput(Controllable(UsesToday(UIDateInput), ['calendarPage', 'focusedValue', 'open', 'value']));